import { useState, useEffect, useCallback, useRef } from 'react';
import { listUsers, type ListUsersParams } from '../api/superAdminApi';
import type { UserRow } from '../types';

interface UsePlatformUsersResult {
  users: UserRow[];
  total: number;
  page: number;
  loading: boolean;
  error: string | null;
  search: string;
  roleFilter: string;
  statusFilter: string;
  setSearch: (s: string) => void;
  setRoleFilter: (r: string) => void;
  setStatusFilter: (s: string) => void;
  setPage: (p: number) => void;
  refresh: () => void;
}

const LIMIT = 50;
const DEBOUNCE_MS = 300;

export function usePlatformUsers(): UsePlatformUsersResult {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearchRaw] = useState('');
  const [roleFilter, setRoleFilterRaw] = useState('');
  const [statusFilter, setStatusFilterRaw] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const doFetch = useCallback(async (params: ListUsersParams) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const thisRequestId = ++requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const data = await listUsers(params, controller.signal);
      if (thisRequestId !== requestIdRef.current) return;
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (err: unknown) {
      if (thisRequestId !== requestIdRef.current) return;
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      if (thisRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const buildParams = useCallback(
    (): ListUsersParams => ({
      search: search || undefined,
      role: roleFilter || undefined,
      status: statusFilter || undefined,
      page,
      limit: LIMIT,
    }),
    [search, roleFilter, statusFilter, page]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doFetch(buildParams()), DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, roleFilter, statusFilter, page, doFetch, buildParams]);

  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const setSearch = useCallback((s: string) => { setSearchRaw(s); setPage(1); }, []);
  const setRoleFilter = useCallback((r: string) => { setRoleFilterRaw(r); setPage(1); }, []);
  const setStatusFilter = useCallback((s: string) => { setStatusFilterRaw(s); setPage(1); }, []);

  return {
    users, total, page, loading, error,
    search, roleFilter, statusFilter,
    setSearch, setRoleFilter, setStatusFilter,
    setPage,
    refresh: () => doFetch(buildParams()),
  };
}
